import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { FlexColumn, FlexRow } from '../../components/atoms/layout/Flex'
import usePage from '../../hooks/usePage'
import Link from '../../components/atoms/navigation/Link'
import { BackIcon } from '../../components/atoms/display/Icons'
import Label from '../../components/atoms/display/Label'
import usePath from '../../hooks/usePath'
import SectionPanel from '../../components/molecules/SectionPanel'
import ClientSearchForm from '../../components/organisms/client/ClientSearchForm'
import ClientSearchResultCard from '../../components/organisms/client/ClientSearchResultCard'
import Button from '../../components/atoms/inputs/Button'
import Select from '../../components/atoms/inputs/Select'
import { useClientMoneyspaceBulkChatDialog } from '../../hooks/useDialog'
import { CompanySelectors } from '../../store/company/selectors'
import CircularProgress from '../../components/atoms/feedback/CircularProgress'
import { CompanyActions } from '../../store/company'
import { MoneyspacesSelectors } from '../../store/moneyspaces/selectors'
import { ClientDetail } from '../../types/Company'

export default function ClientDetailSearchPage() {
  const { changeTitle } = usePage()
  const { settingsClients } = usePath()
  const clientMoneyspaceBulkChatDialog = useClientMoneyspaceBulkChatDialog()
  const clientDetails = useSelector(CompanySelectors.clientDetails)
  const moneyspaces = useSelector(MoneyspacesSelectors.moneyspaces)
  const dispatch = useDispatch()
  const [keyword, setKeyword] = useState<string | undefined>()
  const [licenseNumber, setLicenseNumber] = useState<number>(2)
  const [evaluation, setEvaluation] = useState<number>(0)
  const [areas, setAreas] = useState<string[]>([])
  const [constructions, setConstructions] = useState<string[]>([])
  const [sort, setSort] = useState<number>(1)
  const [filteredClientDetails, setFilteredClientDetails] = useState<ClientDetail[]>(clientDetails ?? [])
  const [selectedMoneyspaceIds, setSelectedMoneyspaceIds] = useState<string[]>([])

  useEffect(() => {
    changeTitle('取引先詳細検索')
  }, [changeTitle])

  useEffect(() => {
    dispatch(CompanyActions.fetchClientDetails())
  }, [dispatch])

  const handleChangeSearchConditions = useCallback(
    (kw: string | null, license: number, evaluationValue: number, areaIds: string[], constructionIds: string[]) => {
      setKeyword(kw ?? undefined)
      setLicenseNumber(license)
      setEvaluation(evaluationValue)
      setAreas(areaIds)
      setConstructions(constructionIds)
    },
    []
  )

  useEffect(() => {
    if (clientDetails === undefined) {
      return
    }
    const clients = clientDetails.filter((client) => {
      if (keyword) {
        if (
          !client.name.includes(keyword) &&
          !client.representativeName?.includes(keyword) &&
          !client.address?.includes(keyword) &&
          !client.phoneNumber?.includes(keyword) &&
          !client.phoneNumberMobile?.includes(keyword) &&
          !client.invoice_license_number?.includes(keyword)
        ) {
          return false
        }
      }
      if (licenseNumber === 1) {
        if (client.invoice_license_number === null) {
          return false
        }
      }
      if (licenseNumber === 0) {
        if (client.invoice_license_number !== null) {
          return false
        }
      }
      if (evaluation > 0) {
        if (!client.evaluation || client.evaluation.totalPoint < evaluation) {
          return false
        }
      }
      if (areas.length > 0) {
        if (!areas.some((area) => client.workAreaIds.includes(area))) {
          return false
        }
      }
      if (constructions.length > 0) {
        const constructionIds = client.constructions.map((construction) => construction.typeId)
        if (!constructions.some((construction) => constructionIds.includes(construction))) {
          return false
        }
      }
      return true
    })
    const sortedClients = clients.sort((detail1, detail2) => {
      if ([1, 2].includes(sort)) {
        const direction = sort === 1 ? 1 : -1
        if (detail1.evaluation == null) {
          if (detail2.evaluation != null) {
            return 1 * direction
          }
        } else if (detail2.evaluation != null) {
          return (detail2.evaluation.totalPoint - detail1.evaluation.totalPoint) * direction
        } else if (detail2.evaluation == null) {
          return -1 * direction
        }
      }
      if ([3, 4].includes(sort)) {
        const direction = sort === 3 ? 1 : -1
        return (dayjs(detail1.createdAt).isAfter(dayjs(detail2.createdAt)) ? 1 : -1) * direction
      }
      return 0
    })
    setFilteredClientDetails(sortedClients)
  }, [areas, clientDetails, constructions, evaluation, keyword, licenseNumber, sort])

  const handleSelectMoneyspace = useCallback(
    (selected: boolean, moneyspaceId: string) => {
      if (selected) {
        setSelectedMoneyspaceIds([...selectedMoneyspaceIds, moneyspaceId])
      } else {
        setSelectedMoneyspaceIds(selectedMoneyspaceIds.filter((id) => id !== moneyspaceId))
      }
    },
    [selectedMoneyspaceIds]
  )

  return (
    <FlexColumn>
      <Link href={settingsClients()}>
        <FlexRow>
          <BackIcon size={16} />
          <Label text="取引先管理" variant="caption" />
        </FlexRow>
      </Link>
      <FlexColumn align="center">
        <SectionPanel title="検索条件">
          <ClientSearchForm
            keyword={keyword}
            licenseNumber={licenseNumber}
            evaluation={evaluation}
            areas={areas}
            constructions={constructions}
            onChange={handleChangeSearchConditions}
          />
        </SectionPanel>
        {clientDetails ? (
          <FlexColumn>
            <FlexRow align="center" justify="space-between">
              <Label text="検索結果" variant="subtitle" />
              <FlexRow>
                <Select
                  value={sort}
                  size="sm2"
                  items={[
                    { label: '評価が高い順', value: 1 },
                    { label: '評価が低い順', value: 2 },
                    { label: '登録が古い順', value: 3 },
                    { label: '登録が新しい順', value: 4 },
                  ]}
                  onChange={setSort}
                />
                <Button
                  size="lg"
                  variant="outlined"
                  disabled={selectedMoneyspaceIds.length === 0}
                  onClick={() => clientMoneyspaceBulkChatDialog.open(selectedMoneyspaceIds)}
                >
                  メッセージ一斉送信
                </Button>
              </FlexRow>
            </FlexRow>

            <FlexColumn>
              {filteredClientDetails.map((detail) => (
                <ClientSearchResultCard
                  key={detail.id}
                  clientDetail={detail}
                  moneyspaces={moneyspaces.filter((moneyspace) => detail.moneyspaceIds.includes(moneyspace.id))}
                  selectedMoneyspaceIds={selectedMoneyspaceIds}
                  highlightAreaIds={areas}
                  highlightConstructionTypeIds={constructions}
                  onSelect={handleSelectMoneyspace}
                />
              ))}
            </FlexColumn>
          </FlexColumn>
        ) : (
          <FlexRow align="center" justify="center" pa={4}>
            <CircularProgress size="normal" />
          </FlexRow>
        )}
      </FlexColumn>
    </FlexColumn>
  )
}
